import { useMutation, UseMutationOptions, useQuery } from '@tanstack/react-query';
import { useAddSnackbarMessage } from '@shared/useAddSnackBar';
import { deleteService, getServices, postCreateOrUpdateService } from './api';
import { Service, ServiceCreateOrUpdateData } from './entity';
import { queryClient } from '../../shared/queries';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { useAnalytics } from '@libs/mixpanel/useAnalytics';
import { isDemo } from '@features/demo/store';

export const useServicesQuery = (enableAllowedServices: boolean = true) => {
  return useQuery<Service[]>({
    queryKey: ['services'],
    queryFn: () => getServices(),
    enabled: enableAllowedServices,
  });
};

export const useCreateNewOrUpdateServiceMutation = (
  options?: UseMutationOptions<ServiceCreateOrUpdateData, Error, ServiceCreateOrUpdateData>,
) => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations('service.mutation');
  const { trackEvent, trackOnboardingStep } = useAnalytics();

  return useMutation<ServiceCreateOrUpdateData, Error, ServiceCreateOrUpdateData>({
    mutationFn: (serviceData: ServiceCreateOrUpdateData) => {
      return postCreateOrUpdateService(serviceData);
    },
    onSuccess: (data, variables, context, meta) => {
      options?.onSuccess?.(data, variables, context, meta);
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
      if (!variables.id) {
        trackOnboardingStep('serviceCreated', 5);
      }
      trackEvent('service_durations_configured', {
        services_edited_count: 1,
        duration_source: 'manual',
      });
      addSnackBarMessage({ text: t('upsertSuccess'), type: 'success' });
    },

    onError: error => {
      addSnackBarMessage({ text: t('upsertError'), type: 'error' });
    },
  });
};

export const useDeleteServiceMutation = (options?: UseMutationOptions<void, Error, string>) => {
  const addSnackBarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations('service.mutation');

  return useMutation({
    mutationFn: (serviceId: string) => deleteService(serviceId),
    onSuccess(data, variables, context, meta) {
      options?.onSuccess?.(data, variables, context, meta);
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
      addSnackBarMessage({ text: t('deleteSuccess'), type: 'success' });
    },
    onError: error => {
      addSnackBarMessage({ text: t('deleteError'), type: 'error' });
    },
  });
};
